import React, { useState } from "react";

import { ExclamationCircleFilled } from "@ant-design/icons";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormLabel } from "@impero-com/ui";
import { Tabs } from "antd";
import { cx } from "class-variance-authority";
import { Tab } from "rc-tabs/es/interface";
import { Control, useController } from "react-hook-form";

import { UserOverview, SavedBlob } from "api/generated/model";
import { RHFAccessRights } from "components/form/rhfFields/rhfAccessRights";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { TestingProgramAccessRightsInstance } from "utils/accessRights/models";

import { ControlConclusionFieldBuilder } from "./components/ControlConclusionBuilder/ControlConclusionBuilder";
import { SampleConclusionFieldBuilder } from "./components/ControlConclusionBuilder/SampleConclusionBuilder";
import {
    TESTING_PROGRAM_ACCESS_RIGHTS_ANCHOR,
    TESTING_PROGRAM_DETAILS_ANCHOR,
    TESTING_PROGRAM_TASKS_ANCHOR,
    useTestingProgramViewAnchorLabels,
} from "./consts";
import * as theme from "./TestingProgramForm.css";
import {
    TestingProgramFormSchema,
    getNameSchema,
    descriptionSchema,
} from "./useTestingProgramViewFormSchema";

interface TestingProgramFormProps {
    control: Control<TestingProgramFormSchema>;
    users: UserOverview[];
    canEdit: boolean;
    canEditAccessRights: boolean;
    blobs: SavedBlob[];
}
export function TestingProgramForm({
    control,
    users,
    canEdit,
    canEditAccessRights,
    blobs,
}: TestingProgramFormProps) {
    const { t } = useTranslation();
    const anchorLabels = useTestingProgramViewAnchorLabels();

    return (
        <div>
            <FormSection
                id={TESTING_PROGRAM_DETAILS_ANCHOR}
                titleLabel={anchorLabels[TESTING_PROGRAM_DETAILS_ANCHOR]}
                withDivider
            >
                <RHFStringInput
                    label={t("TestingProgramView_Name_Label")}
                    placeholder={t("TestingProgramView_Name_Placeholder")}
                    control={control}
                    name="name"
                    schema={getNameSchema(t)}
                    disabled={!canEdit}
                />
                <RHFMarkdownInput
                    label={t("TestingProgramView_Description_Label")}
                    placeholder={t(
                        "TestingProgramView_Description_Placeholder"
                    )}
                    control={control}
                    name="description"
                    schema={descriptionSchema}
                    disabled={!canEdit}
                />
            </FormSection>
            <FormSection
                id={TESTING_PROGRAM_ACCESS_RIGHTS_ANCHOR}
                titleLabel={anchorLabels[TESTING_PROGRAM_ACCESS_RIGHTS_ANCHOR]}
                withDivider
            >
                <FormLabel>{t("AccessRights_AccessManagement")}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={users}
                    name="accessRights"
                    resourceAccessRights={TestingProgramAccessRightsInstance}
                    disabled={!canEditAccessRights}
                />
            </FormSection>
            <FormSection
                id={TESTING_PROGRAM_TASKS_ANCHOR}
                titleLabel={anchorLabels[TESTING_PROGRAM_TASKS_ANCHOR]}
            >
                <ConclusionTasks
                    disabled={!canEdit}
                    control={control}
                    blobs={blobs}
                />
            </FormSection>
        </div>
    );
}
type ActiveTab = "controlConclusionTasks" | "sampleConclusionTasks";

interface TabOption {
    label: string;
    value: ActiveTab;
    hasError: boolean;
    fieldBuilderElement: React.ReactNode;
}
interface ConclusionTasksProps {
    disabled: boolean;
    control: Control<TestingProgramFormSchema>;
    blobs: SavedBlob[] | null;
}
function ConclusionTasks({ disabled, control, blobs }: ConclusionTasksProps) {
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState<ActiveTab>(
        "controlConclusionTasks"
    );

    const {
        fieldState: controlConclusionFieldState,
        field: controlConclusionField,
    } = useController({ control, name: "controlConclusionTasks", disabled });
    const {
        fieldState: sampleConclusionTasksState,
        field: sampleConclusionField,
    } = useController({ control, name: "sampleConclusionTasks", disabled });

    const options: TabOption[] = [
        {
            label: t("TestingProgramView_ControlConclusionTasks_Tab"),
            value: "controlConclusionTasks",
            hasError: Boolean(controlConclusionFieldState.error),
            fieldBuilderElement: (
                <ControlConclusionFieldBuilder
                    blobs={blobs ?? []}
                    disabled={controlConclusionField.disabled}
                    control={control}
                />
            ),
        },
        {
            label: t("TestingProgramView_SampleConclusionTasks_Tab"),
            value: "sampleConclusionTasks",
            hasError: Boolean(sampleConclusionTasksState.error),
            fieldBuilderElement: (
                <SampleConclusionFieldBuilder
                    blobs={blobs ?? []}
                    disabled={sampleConclusionField.disabled}
                    control={control}
                />
            ),
        },
    ];

    const handleTabChange = (activeTab: string) => {
        controlConclusionField.onBlur();
        sampleConclusionField.onBlur();
        setActiveTab(activeTab as ActiveTab);
    };

    const items: Tab[] = options.map((option) => {
        const showError = option.hasError && option.value !== activeTab;
        return {
            key: option.value,
            label: (
                <span
                    className={cx(
                        showError && ERRORED_CLASSNAME,
                        showError && theme.buttonGroupError
                    )}
                >
                    {showError && <ExclamationCircleFilled />}
                    {option.label}
                </span>
            ),
            children: option.fieldBuilderElement,
        };
    });

    return <Tabs items={items} onChange={handleTabChange} type="card" />;
}
